import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, MousePointer, Eye, CreditCard, CheckCircle, Shield, AlertCircle } from 'lucide-react';

const CustomerPortalGuide = () => {
  const navigate = useNavigate();

  return (
    <div className="p-4 lg:p-8 pb-20 lg:pb-8">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate('/help')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <ArrowLeft size={20} />
          <span>Back to Help Center</span>
        </button>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-3">Customer Portal Guide</h1>
          <p className="text-lg text-gray-600">
            Learn how to give your customers secure access to view their jobs and invoices online.
          </p>
        </div>

        {/* What is the Customer Portal */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-3">What is the Customer Portal?</h2>
          <p className="text-gray-700 mb-4">
            The Customer Portal is a secure website where your customers can:
          </p>
          <ul className="space-y-3">
            <li className="flex items-start gap-3">
              <CheckCircle size={20} className="text-green-600 mt-0.5 flex-shrink-0" />
              <span className="text-gray-700">View all their scheduled and completed jobs</span>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle size={20} className="text-green-600 mt-0.5 flex-shrink-0" />
              <span className="text-gray-700">See their invoices and payment history</span>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle size={20} className="text-green-600 mt-0.5 flex-shrink-0" />
              <span className="text-gray-700">Request new service quotes</span>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle size={20} className="text-green-600 mt-0.5 flex-shrink-0" />
              <span className="text-gray-700">Pay invoices online (coming soon!)</span>
            </li>
          </ul>
        </div>

        {/* How to Send Access Links */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mb-6">
          <div className="bg-gradient-to-r from-primary-50 to-primary-100 px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900">How to Give Customers Access</h2>
          </div>
          
          <div className="p-6 space-y-6">
            {/* Step 1 */}
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0">
                1
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-2">Go to Customers Page</h3>
                <p className="text-gray-700">
                  Click on "Customers" in your main menu to see your customer list.
                </p>
              </div>
            </div>

            {/* Step 2 */}
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0">
                2
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-2">Find the Customer</h3>
                <p className="text-gray-700">
                  Search for or scroll to find the customer you want to give access to.
                </p>
              </div>
            </div>

            {/* Step 3 */}
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0">
                3
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-2">Click "Send Portal Access"</h3>
                <p className="text-gray-700 mb-3">
                  Click the button next to their name. We'll send them a secure email with a special access link.
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <Mail size={18} className="text-blue-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-blue-900 font-medium mb-1">
                        Important: Customer must have an email address
                      </p>
                      <p className="text-sm text-blue-700">
                        Make sure the customer has an email address in their profile before sending access.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 4 */}
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0">
                4
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-2">Customer Receives Email</h3>
                <p className="text-gray-700 mb-3">
                  Your customer will receive an email with a button to access their portal.
                </p>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <p className="text-sm font-medium text-gray-900 mb-3">Sample Email:</p>
                  <div className="bg-white border border-gray-300 rounded-lg p-4">
                    <p className="text-sm text-gray-600 mb-2">
                      <strong>Subject:</strong> Access Your Customer Portal
                    </p>
                    <p className="text-sm text-gray-700 mb-4">
                      Hi John,<br/><br/>
                      Click below to view your jobs and invoices:
                    </p>
                    <button className="bg-primary-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors cursor-not-allowed">
                      Access My Portal
                    </button>
                    <p className="text-xs text-gray-500 mt-3">
                      This link expires in 1 hour for your security.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 5 */}
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-green-600 text-white rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0">
                ✓
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-2">Done!</h3>
                <p className="text-gray-700">
                  Your customer can now view their information anytime. The link expires after 1 hour for security, 
                  but they can request a new link whenever they need it.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Security & Privacy */}
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center">
              <Shield className="text-white" size={20} />
            </div>
            <h2 className="text-xl font-bold text-gray-900">Security & Privacy</h2>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <CheckCircle size={20} className="text-green-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-gray-900">Secure Access Links</p>
                <p className="text-sm text-gray-700">
                  Each access link is unique and expires after 1 hour for security. Customers can request new links anytime.
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <CheckCircle size={20} className="text-green-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-gray-900">Private Data</p>
                <p className="text-sm text-gray-700">
                  Customers can only see their own jobs and invoices - never other customers' data. Complete isolation.
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <CheckCircle size={20} className="text-green-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-gray-900">View-Only Access</p>
                <p className="text-sm text-gray-700">
                  Customers can view information but cannot edit or delete anything. Your data stays safe.
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <CheckCircle size={20} className="text-green-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-gray-900">Activity Logging</p>
                <p className="text-sm text-gray-700">
                  All customer portal access is logged with timestamps and IP addresses for your records.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* What Customers Can See */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">What Customers Can See</h2>
          
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <Eye className="text-blue-600 mt-1 flex-shrink-0" size={20} />
              <div>
                <p className="font-medium text-gray-900 mb-1">Jobs Dashboard</p>
                <p className="text-sm text-gray-700">
                  View all scheduled, in-progress, and completed jobs with dates, addresses, and service details.
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <Eye className="text-blue-600 mt-1 flex-shrink-0" size={20} />
              <div>
                <p className="font-medium text-gray-900 mb-1">Invoice List</p>
                <p className="text-sm text-gray-700">
                  See all invoices with status (paid/unpaid), amounts, and due dates. Filter by paid or unpaid.
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <Eye className="text-blue-600 mt-1 flex-shrink-0" size={20} />
              <div>
                <p className="font-medium text-gray-900 mb-1">Invoice Details</p>
                <p className="text-sm text-gray-700">
                  Click any invoice to see line-item details, payment history, and download options.
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <MousePointer className="text-blue-600 mt-1 flex-shrink-0" size={20} />
              <div>
                <p className="font-medium text-gray-900 mb-1">Request New Service</p>
                <p className="text-sm text-gray-700">
                  Submit quote requests for new services directly through the portal. You'll be notified immediately.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Common Questions */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Common Questions</h2>
          
          <div className="space-y-5">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">
                What if the customer's link expires?
              </h3>
              <p className="text-sm text-gray-700">
                No problem! Just send them a new access link. Go to the Customers page and click "Send Portal Access" again. 
                They'll receive a fresh email with a new link instantly.
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">
                Can customers edit their information?
              </h3>
              <p className="text-sm text-gray-700">
                No - the portal is view-only. Customers can see their jobs and invoices but cannot make changes, delete anything, 
                or see other customers' data. This keeps your data secure and prevents accidental changes.
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">
                What if the customer doesn't have an email?
              </h3>
              <p className="text-sm text-gray-700">
                You'll need to add an email address to their customer profile first. Click "Edit" on their customer 
                record, add their email address, save, and then you can send portal access.
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">
                Can I send portal access to multiple customers at once?
              </h3>
              <p className="text-sm text-gray-700">
                Not yet, but this feature is coming soon! For now, you'll need to send access individually from each 
                customer's profile. It only takes a few seconds per customer.
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">
                How do I know if a customer used their portal?
              </h3>
              <p className="text-sm text-gray-700">
                Coming soon! We're building activity reports that will show you when customers access their portal, 
                what they viewed, and when they requested new quotes.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">
                Can customers pay invoices through the portal?
              </h3>
              <p className="text-sm text-gray-700">
                Not yet - this feature is coming in Q1 2026! Customers will be able to pay invoices with credit cards 
                directly from the portal. For now, they can view invoices and you can record payments manually.
              </p>
            </div>
          </div>
        </div>

        {/* Best Practices */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">💡 Best Practices</h2>
          <div className="space-y-3">
            <div>
              <h3 className="font-semibold text-amber-900 mb-1">Send Access Proactively</h3>
              <p className="text-sm text-amber-800">
                Send portal access when you first complete a job or send an invoice. Customers appreciate being able 
                to track their service history online.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-amber-900 mb-1">Mention It in Person</h3>
              <p className="text-sm text-amber-800">
                Tell customers "I'll send you a link where you can view your invoice and service history online." 
                This sets the expectation and makes them more likely to use it.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-amber-900 mb-1">Keep Email Addresses Updated</h3>
              <p className="text-sm text-amber-800">
                Make sure you collect email addresses from all customers. This enables portal access AND automated 
                invoice emails - saving you time on every job.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-amber-900 mb-1">Use It to Reduce Phone Calls</h3>
              <p className="text-sm text-amber-800">
                When customers call asking "Did you invoice me?" or "When is my next appointment?", you can say 
                "Check your customer portal - all your information is there!" Saves you time answering repetitive questions.
              </p>
            </div>
          </div>
        </div>

        {/* Need Help */}
        <div className="bg-gradient-to-r from-primary-50 to-primary-100 border border-primary-200 rounded-lg p-6 text-center mb-8">
          <h3 className="text-xl font-bold text-gray-900 mb-2">Need Help?</h3>
          <p className="text-gray-700 mb-4">
            If you have questions about the Customer Portal or need assistance, we're here to help!
          </p>
          <a
            href="mailto:support@suddyflow.com"
            className="btn btn-primary inline-flex items-center gap-2"
          >
            <Mail size={18} />
            Email Support
          </a>
        </div>

        {/* Back to Help Center */}
        <div className="text-center">
          <button
            onClick={() => navigate('/help')}
            className="text-primary-600 hover:text-primary-700 font-medium"
          >
            ← Back to Help Center
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomerPortalGuide;